<template>
  <main id="magazzino" class="page-container stock-list">
    <div class="table-container-stock">
      <div class="magazzino-filters">
        <input
          v-model="filterBrand"
          class="filter-input"
          placeholder="Filtra per brand…"
          type="text"
        />
        <input
          v-model="filterCode"
          class="filter-input"
          placeholder="Filtra per codice…"
          type="text"
        />
        <input
          v-model="filterName"
          class="filter-input"
          placeholder="Filtra per nome…"
          type="text"
        />
        <select v-model="filterCategory" class="filter-select">
          <option value="">Tutte le categorie</option>
          <option v-for="cat in CATEGORIES" :key="cat" :value="cat">
            {{ cat }}
          </option>
        </select>
        <label class="filter-checkbox">
          <input type="checkbox" v-model="filterInStock" />
          Disponibili in magazzino
        </label>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
          style="margin-left: auto"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <div class="tbl-wrap">
        <table class="tbl item-table">
          <thead>
            <tr>
              <th>Codice</th>
              <th>Nome</th>
              <th>Brand</th>
              <th v-if="!filterCategory">Categoria</th>
              <th class="col-giacenza">Giacenza</th>
              <th v-if="showBoxCol">{{ boxColLabel }}</th>
              <th v-if="showSqmCol">M² tot.</th>
              <th v-if="showMlCol">ML tot.</th>
              <th style="text-align: left">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredItems"
              :key="item.id"
              class="item-row"
              :class="{ 'item-row--critical': criticalItemIds[item.id] }"
            >
              <td class="td-name" :title="item.itemCode">
                {{ item.itemCode }}
              </td>
              <td class="td-name" :title="item.itemName">
                {{ item.itemName }}
              </td>
              <td>{{ item.brand ?? "—" }}</td>
              <td v-if="!filterCategory">{{ item.category }}</td>
              <td class="col-giacenza">
                {{ item.warehouse?.inWarehouse ?? 0 }}
                <span
                  v-if="
                    !['area', 'linear'].includes(itemGroup(item)) &&
                    item.totalDamagedQty > 0
                  "
                  class="damaged-badge"
                  :title="`${item.totalDamagedQty} pz danneggiati su carichi merce ricevuti`"
                >
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  {{ item.totalDamagedQty }}
                </span>
              </td>
              <td v-if="showBoxCol">
                {{
                  ["area", "linear"].includes(itemGroup(item))
                    ? item.warehouse?.boxQuantity ?? "—"
                    : "—"
                }}
                <span
                  v-if="
                    ['area', 'linear'].includes(itemGroup(item)) &&
                    item.totalDamagedQty > 0
                  "
                  class="damaged-badge"
                  :title="`${item.totalDamagedQty} sc. danneggiate su carichi merce ricevuti`"
                >
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  {{ item.totalDamagedQty }}
                </span>
              </td>
              <td v-if="showSqmCol">
                {{
                  itemGroup(item) === "area" &&
                  Number(item.warehouse?.sqmTotal) > 0
                    ? Number(item.warehouse.sqmTotal).toFixed(2)
                    : "—"
                }}
              </td>
              <td v-if="showMlCol">
                {{
                  itemGroup(item) === "linear" &&
                  Number(item.warehouse?.mlTotal) > 0
                    ? Number(item.warehouse.mlTotal).toFixed(2)
                    : "—"
                }}
              </td>
              <td class="actions-cell">
                <button
                  class="icons-button"
                  @click="openEdit(item)"
                  title="Modifica magazzino"
                  type="button"
                >
                  <i class="fa-regular fa-pen-to-square"></i>
                </button>
                <button
                  class="icons-button"
                  @click="openHistory(item)"
                  title="Storico movimenti"
                  type="button"
                >
                  <i class="fa-regular fa-clock"></i>
                </button>
                <button
                  class="icons-button"
                  :class="{ 'btn-critical--active': criticalItemIds[item.id] }"
                  @click="toggleCritical(item.id)"
                  title="Segnala criticità"
                  type="button"
                >
                  <i class="fa-solid fa-triangle-exclamation"></i>
                </button>
              </td>
            </tr>
            <tr v-if="filteredItems.length === 0">
              <td
                :colspan="colCount"
                style="text-align: center; padding: 16px; color: #888"
              >
                Nessun articolo trovato.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <div class="pagination-left"></div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="pagination.currentPage === pagination.totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal modifica magazzino -->
    <div
      v-if="editItem"
      class="warehouse-modal-backdrop"
      @click.self="closeEdit"
    >
      <div class="warehouse-modal">
        <button
          class="icons-button warehouse-modal__close"
          @click="closeEdit"
          type="button"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="warehouse-modal__title">Aggiorna magazzino</h3>
        <p class="warehouse-modal__subtitle">{{ editItem.itemName }}</p>

        <div class="warehouse-modal__fields">
          <template v-if="editGroup === 'area'">
            <div class="field">
              <label>Numero scatole</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.boxQuantity"
              />
              <small>Pezzi e m² calcolati automaticamente</small>
            </div>
          </template>
          <template v-else-if="editGroup === 'linear'">
            <div class="field">
              <label>Numero confezioni</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.boxQuantity"
              />
              <small>Metri lineari calcolati automaticamente</small>
            </div>
          </template>
          <template v-else>
            <div class="field">
              <label>Pezzi in magazzino</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.inWarehouse"
              />
            </div>
          </template>
        </div>

        <div class="warehouse-modal__actions">
          <button class="btn btn-secondary" @click="closeEdit" type="button">
            Annulla
          </button>
          <button
            class="btn btn-primary"
            @click="saveEdit"
            type="button"
            :disabled="editSaving"
          >
            {{ editSaving ? "Salvataggio…" : "Salva" }}
          </button>
        </div>
      </div>
    </div>

    <ItemHistoryModal
      v-if="historyItem"
      :item-id="historyItem.id"
      :item-name="historyItem.name"
      :category="historyItem.category"
      :show="!!historyItem"
      @update:show="closeHistory"
    />
  </main>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import ItemHistoryModal from "../components/ItemHistoryModal.vue";

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  linear: ["profili", "sistemi di scarico"],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  furniture: ["mobili", "porte e infissi"],
};

function getCategoryGroup(category, salesUnit) {
  if (category === "piastrelle" && salesUnit === "ML") return "linear";
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

const CATEGORIES = [
  "piastrelle",
  // "rivestimenti parete",
  // "parquet",
  // "laminati",
  // "decking esterno",
  // "box doccia",
  // "sanitari",
  // "rubinetteria",
  // "accessori bagno",
  // "profili",
  // "sistemi di scarico",
  // "mobili",
  // "porte e infissi",
  // "altro",
];

export default {
  name: "MagazzinoPage",
  components: { ItemHistoryModal },
  data() {
    return {
      CATEGORIES,
      refreshing: false,
      refreshed: false,
      items: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 8,
        startItem: 1,
        endItem: 0,
      },
      filterBrand: "",
      filterCode: "",
      filterName: "",
      filterCategory: "",
      filterInStock: true,
      historyItem: null,
      editItem: null,
      editFields: { boxQuantity: 0, inWarehouse: 0 },
      editSaving: false,
      criticalItemIds: {},
    };
  },
  computed: {
    filteredItems() {
      return this.items;
    },
    activeGroup() {
      return this.filterCategory ? getCategoryGroup(this.filterCategory) : null;
    },
    showBoxCol() {
      return (
        !this.activeGroup ||
        this.activeGroup === "area" ||
        this.activeGroup === "linear"
      );
    },
    showSqmCol() {
      return !this.activeGroup || this.activeGroup === "area";
    },
    showMlCol() {
      return (
        !this.activeGroup ||
        this.activeGroup === "linear" ||
        this.filterCategory === "piastrelle"
      );
    },
    boxColLabel() {
      return this.activeGroup === "linear"
        ? "N. Confezioni in stock"
        : "N. Scatole in stock";
    },
    colCount() {
      let n = 5; // Codice, Nome, Brand, Giacenza, actions
      if (!this.filterCategory) n++; // Categoria
      if (this.showBoxCol) n++;
      if (this.showSqmCol) n++;
      if (this.showMlCol) n++;
      return n;
    },
    editGroup() {
      return getCategoryGroup(
        this.editItem?.category,
        this.editItem?.salesUnit
      );
    },
    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  methods: {
    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.loadItems(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    async toggleCritical(id) {
      try {
        const { data } = await axiosInstance.patch(`/item/${id}/critical`);
        this.criticalItemIds = {
          ...this.criticalItemIds,
          [id]: data.isCritical,
        };
      } catch {
        this.$toast.error("Errore durante l'aggiornamento della criticità.");
      }
    },
    itemGroup(item) {
      return getCategoryGroup(item.category, item.salesUnit);
    },
    openHistory(item) {
      this.historyItem = {
        id: item.id,
        name: item.itemName,
        category: item.category,
      };
    },
    closeHistory() {
      this.historyItem = null;
    },

    openEdit(item) {
      this.editItem = item;
      const wh = item.warehouse ?? {};
      this.editFields = {
        boxQuantity: wh.boxQuantity ?? 0,
        inWarehouse: wh.inWarehouse ?? 0,
      };
    },
    closeEdit() {
      this.editItem = null;
      this.editSaving = false;
    },
    async saveEdit() {
      this.editSaving = true;
      try {
        const group = this.editGroup;
        const payload = {};
        if (group === "area" || group === "linear") {
          payload.boxQuantity = this.editFields.boxQuantity;
        } else {
          payload.inWarehouse = this.editFields.inWarehouse;
        }
        await axiosInstance.patch(`/item/update/${this.editItem.id}`, payload);
        this.$toast.success("Magazzino aggiornato.");
        this.closeEdit();
        this.loadItems(this.pagination.currentPage);
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          "Errore durante l'aggiornamento del magazzino.";
        this.$toast.warning(msg);
      } finally {
        this.editSaving = false;
      }
    },

    async loadItems(page = 1) {
      try {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: {
            page,
            limit: 8,
            ...(this.filterCategory ? { category: this.filterCategory } : {}),
            ...(this.filterBrand ? { brand: this.filterBrand } : {}),
            ...(this.filterCode ? { itemCode: this.filterCode } : {}),
            ...(this.filterName ? { itemName: this.filterName } : {}),
            ...(this.filterInStock ? { inStock: true } : {}),
          },
        });
        this.items = Array.isArray(data.results) ? data.results : [];
        const newCritical = {};
        this.items.forEach((item) => {
          if (item.warehouse?.isCritical) newCritical[item.id] = true;
        });
        this.criticalItemIds = { ...this.criticalItemIds, ...newCritical };
        const p = data.pagination;
        this.pagination = {
          currentPage: p.currentPage,
          totalPages: p.totalPages,
          totalItems: p.totalItems,
          itemsPerPage: p.itemsPerPage,
          startItem: (p.currentPage - 1) * p.itemsPerPage + 1,
          endItem: Math.min(p.currentPage * p.itemsPerPage, p.totalItems),
        };
      } catch (err) {
        if (err.response?.status === 404) {
          this.items = [];
          return;
        }
        this.$toast.error("Errore durante il caricamento del magazzino.");
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.loadItems(page);
    },
  },
  mounted() {
    this.loadItems(1);
  },
  watch: {
    filterCategory() {
      this.loadItems(1);
    },
    filterBrand() {
      this.loadItems(1);
    },
    filterCode() {
      this.loadItems(1);
    },
    filterName() {
      this.loadItems(1);
    },
    filterInStock() {
      this.loadItems(1);
    },
  },
};
</script>

<style src="@/css/StockPage.css" />
<style scoped>
.warehouse-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.warehouse-modal {
  background: #fff;
  border-radius: 8px;
  padding: 28px 32px;
  min-width: 320px;
  max-width: 420px;
  width: 100%;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.warehouse-modal__close {
  position: absolute;
  top: 12px;
  right: 12px;
}
.warehouse-modal__title {
  margin: 0 0 4px;
  font-size: 1.1rem;
}
.warehouse-modal__subtitle {
  margin: 0 0 20px;
  color: #666;
  font-size: 0.9rem;
}
.warehouse-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
}
.warehouse-modal__fields .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.warehouse-modal__fields label {
  font-size: 0.85rem;
  font-weight: 600;
}
.warehouse-modal__fields input {
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}
.warehouse-modal__fields small {
  font-size: 0.78rem;
  color: #888;
}
.col-giacenza {
  background-color: #fef2f2;
  font-weight: 600;
}
.item-row--critical {
  background-color: #fff8e1 !important;
}
.item-row--critical .col-giacenza {
  background-color: #fff3cd !important;
}
.item-row--critical:hover {
  background-color: #fdefc3 !important;
}
.btn-critical--active {
  color: #e65100;
}
.damaged-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  font: 600 11px "Inter", sans-serif;
  white-space: nowrap;
  cursor: default;
}
.item-row:hover .col-giacenza {
  background-color: #e0e0e0;
}
.warehouse-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
